import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Chip,
  Tooltip,
  Fade,
  TextField,
  InputAdornment,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../../theme";
import {
  AccountTree as AccountTreeIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from "@mui/icons-material";
import { fetchDataEngine, graphqlQuery, graphqlQueryWithVar } from "../../../data/Axios/DynamicService";
import { PulseLoader } from "react-spinners";
import Swal from "sweetalert2";
import { AllChartOfAccountsView } from "../../../data/Axios/queries";
import { accountsmanagement } from "../../../config";
import { format } from "date-fns";

const base_url = accountsmanagement.uri;



const JournalEntriesModal = ({ open, onClose, accountId, accountName, loading, journalData }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm");
    } catch {
      return "Invalid Date";
    }
  };

  const journalColumns = [
    {
      field: "transactionDate",
      headerName: "Transaction Date",
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value || "Journal Entry"}
        </Typography>
      ),
    },
    {
      field: "debit",
      headerName: "Debit",
      width: 120,
      renderCell: (params) => (
        <Typography
          variant="body2"
          color={params.value > 0 ? colors.greenAccent[400] : colors.grey[500]}
          fontWeight="600"
        >
          {params.value > 0 ? formatCurrency(params.value) : "-"}
        </Typography>
      ),
    },
    {
      field: "credit",
      headerName: "Credit",
      width: 120,
      renderCell: (params) => (
        <Typography
          variant="body2"
          color={params.value > 0 ? colors.redAccent[400] : colors.grey[500]}
          fontWeight="600"
        >
          {params.value > 0 ? formatCurrency(params.value) : "-"}
        </Typography>
      ),
    },
    {
      field: "balance",
      headerName: "Running Balance",
      width: 140,
      renderCell: (params) => {
        const row = params.row;
        const runningBalance = row.debit - row.credit;
        return (
          <Chip
            label={formatCurrency(runningBalance)}
            size="small"
            sx={{
              backgroundColor: runningBalance >= 0 ? colors.greenAccent[700] : colors.redAccent[700],
              color: colors.grey[100],
              fontWeight: 600,
            }}
          />
        );
      },
    },
  ];

  const processedJournalData = journalData.map((entry, index) => ({
    id: entry.journalLineID || index,
    transactionDate: entry.createdDate,
    description: entry.description,
    debit: entry.debit,
    credit: entry.credit,
    journalEntryID: entry.journalEntryID,
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          backgroundColor: colors.primary[500],
          height: "85vh", // Increased height to utilize more screen space
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
          color: colors.grey[100],
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2, // Reduced padding for a tighter header
          borderBottom: `1px solid ${colors.primary[300]}`,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight="700">
            Journal Entries
          </Typography>
          <Typography variant="body2" sx={{ color: colors.grey[300], mt: 0.5 }}>
            {accountName}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: colors.grey[100],
            "&:hover": {
              backgroundColor: colors.primary[300],
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0, // Removed padding to maximize table space
          flex: 1, // Allow content to take remaining space
          overflow: "hidden", // Prevent unwanted scrollbars
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%" flexDirection="column" gap={2}>
            <CircularProgress size={40} sx={{ color: colors.greenAccent[500] }} />
            <Typography variant="body1" sx={{ color: colors.grey[300] }}>
              Loading journal entries...
            </Typography>
          </Box>
        ) : (
          <DataGrid
            rows={processedJournalData}
            columns={journalColumns}
            getRowId={(row) => row.id}
            components={{ Toolbar: GridToolbar }}
            disableRowSelectionOnClick
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            sx={{
              height: "100%", // Use full height of DialogContent
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: colors.primary[400],
                borderBottom: `1px solid ${colors.primary[300]}`,
                fontSize: "0.875rem",
                fontWeight: 600,
                color: colors.grey[100],
                minHeight: "40px !important", // Reduced header height
                lineHeight: "1.2 !important",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: `1px solid ${colors.primary[300]}`,
                padding: "8px", // Reduced cell padding for more compact rows
                fontSize: "0.85rem",
              },
              "& .MuiDataGrid-row": {
                minHeight: "36px !important", // Reduced row height for more rows
                maxHeight: "36px !important",
                "&:hover": {
                  backgroundColor: colors.primary[400],
                },
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: `1px solid ${colors.primary[300]}`,
                backgroundColor: colors.primary[400],
                minHeight: "20px !important",
              },
              "& .MuiDataGrid-toolbarContainer": {
                padding: "8px", // Reduced toolbar padding
                backgroundColor: colors.primary[400],
                borderBottom: `1px solid ${colors.primary[300]}`,
                "& .MuiButton-text": {
                  color: colors.grey[100],
                  fontWeight: 600,
                  fontSize: "0.85rem",
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                overflowY: "auto !important", // Ensure scrollbar appears for content
              },
            }}
            hideFooterSelectedRowCount
          />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          bgcolor: colors.primary[400],
          p: 1.5, // Reduced padding for a tighter footer
          borderTop: `1px solid ${colors.primary[300]}`,
        }}
      >
        <Chip
          label={`Total Entries: ${processedJournalData.length}`}
          size="small"
          sx={{
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            fontWeight: 600,
          }}
        />
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: colors.greenAccent[500],
            color: colors.primary[900],
            fontWeight: 600,
            "&:hover": {
              backgroundColor: colors.greenAccent[400],
            },
            padding: "6px 16px", // Smaller button padding
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// export default JournalEntriesModal;

// ... (other imports and components remain the same)

const ChartOfAccountsTable = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [journalModal, setJournalModal] = useState({
    open: false,
    accountId: null,
    accountName: "",
    journalData: [],
    loading: false,
  });

  useEffect(() => {
    fetchChartOfAccounts();
  }, []);

  const fetchChartOfAccounts = async () => {
    try {
      setLoading(true);
      const response = await graphqlQuery(AllChartOfAccountsView, base_url);
      if (response && Array.isArray(response)) {
        // Ensure data is sorted by SQL view's order
        const sortedData = response.sort((a, b) => {
          if (a.groupAccountId !== b.groupAccountId) return a.groupAccountId - b.groupAccountId;
          if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
          if (a.accountSubcategoryId !== b.accountSubcategoryId) return a.accountSubcategoryId - b.accountSubcategoryId;
          return a.id - b.id;
        });
        setChartData(sortedData);
      } else {
        setChartData([]);
      }
    } catch (error) {
      console.error("Error fetching chart of accounts:", error);
      Swal.fire("Error", "Failed to load chart of accounts", "error");
    } finally {
      setLoading(false);
    }
  };
const fetchJournalEntries = async (accountId) => {
    try {
      setJournalModal(prev => ({ ...prev, loading: true }));
      
      const returnFields = [
        "journalLineID",
        "journalEntryID",
        "accountID",
        "debit",
        "credit",
        "description",
        "createdDate",
        "caseId",
        "applicationId",
        "applicationType",
      ];
      
      const inputFields = { 
        accountID: accountId,
        cancelled: null 
      };
      
      const response = await fetchDataEngine(
        "allJournalLinesByColumnValues",
        "journalLines",
        inputFields,
        returnFields,
        base_url
      );
      
      if (response && response && response) {
        setJournalModal(prev => ({
          ...prev,
          journalData: response,
          loading: false,
        }));
      } else {
        setJournalModal(prev => ({ ...prev, journalData: [], loading: false }));
      }
    } catch (error) {
      console.error("Error fetching journal entries:", error);
      Swal.fire("Error", "Failed to load journal entries", "error");
      setJournalModal(prev => ({ ...prev, loading: false }));
    }
  };

  const GROUPED_JOURNAL_LINES_QUERY = `
    query GroupedJournalLinesByColumnValues($accountCategoryId: Int!, $identifierType: Int!) {
      groupedJournalLinesByColumnValues(accountCategoryId: $accountCategoryId, identifierType: $identifierType) {
        journalLineID
        journalEntryID
        accountID
        debit
        credit
        description
        createdDate
        createdBy
        caseId
        cancelled
        applicationId
        applicationType
      }
    }
  `;

  const fetchJournalEntriesForGroupAccounts = async (accountCategoryId, identifierType) => {
    try {
      setJournalModal(prev => ({ ...prev, loading: true }));
      
      const response = await graphqlQueryWithVar(
        GROUPED_JOURNAL_LINES_QUERY,
        base_url,
        {
          accountCategoryId: accountCategoryId,
          identifierType: identifierType
        }
      );
      
      const journalLines = response || [];
      const filteredJournalLines = journalLines.filter(line => 
        line.cancelled === null || line.cancelled === false
      );
      
      setJournalModal(prev => ({
        ...prev,
        journalData: filteredJournalLines,
        loading: false,
      }));
      
    } catch (error) {
      console.error("Error fetching grouped journal entries:", error);
      Swal.fire("Error", "Failed to load journal entries for grouped account", "error");
      setJournalModal(prev => ({ ...prev, journalData: [], loading: false }));
    }
  };
  // ... (fetchJournalEntries and fetchJournalEntriesForGroupAccounts remain unchanged)

  const handleBalanceClick = async (row) => {
    setJournalModal({
      open: true,
      accountId: row.id,
      accountName: row.accountName,
      journalData: [],
      loading: true,
    });

    if (row.isGrouped === 1 || row.isSummary === 1) {
      await fetchJournalEntriesForGroupAccounts(
        row.accountCategoryId, // Use accountSubcategoryId for grouped accounts
        row.identifierType
      );
    } else {
      await fetchJournalEntries(row.id);
    }
  };

  const handleCloseModal = () => {
    setJournalModal((prev) => ({ ...prev, open: false }));
  };

  const getRowsWithStaticHeaders = () => {
    // Group by GroupAccountId, SortOrder, and Category to maintain SQL sorting
    const grouped = chartData.reduce((acc, row) => {
      const key = `${row.groupAccountId}_${row.sortOrder}_${row.category}`;
      if (!acc[key]) {
        acc[key] = {
          groupAccountId: row.groupAccountId,
          sortOrder: row.sortOrder,
          category: row.category,
          accounts: [],
        };
      }
      acc[key].accounts.push(row);
      return acc;
    }, {});

    // Sort groups by GroupAccountId and SortOrder
    const sortedGroups = Object.values(grouped).sort((a, b) => {
      if (a.groupAccountId !== b.groupAccountId) return a.groupAccountId - b.groupAccountId;
      return a.sortOrder - b.sortOrder;
    });

    let processedRows = [];
    let rowIndex = 0;

    sortedGroups.forEach((group) => {
      // Add category header
      processedRows.push({
        id: `header_${group.category}_${rowIndex++}`,
        accountNumber: "",
        accountName: group.category,
        category: group.category,
        isCategoryHeader: true,
        _level: 0,
        rowType: "header",
        groupAccountId: group.groupAccountId,
        sortOrder: group.sortOrder,
      });

      // Add accounts, preserving AccountSubcategoryId and Id sorting
      group.accounts
        .sort((a, b) => {
          if (a.accountSubcategoryId !== b.accountSubcategoryId) return a.accountSubcategoryId - b.accountSubcategoryId;
          return a.id - b.id;
        })
        .forEach((account) => {
          processedRows.push({
            ...account,
            _level: 1,
            rowType: "account",
            parentCategory: group.category,
          });
        });
    });

    return processedRows;
  };

  const getRowId = (row) => row.id;

  const columns = [
    {
      field: "accountNumber",
      headerName: "Account Number",
      width: 150,
      renderCell: (params) => {
        if (params.row.isCategoryHeader) {
          return (
            <Box sx={{ pl: 2, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <AccountTreeIcon sx={{ color: colors.greenAccent[500] }} />
              <Typography variant="h6" fontWeight="bold" color={colors.greenAccent[400]}>
                {params.row.accountName}
              </Typography>
            </Box>
          );
        }
        return (
          <Typography
            variant="body2"
            fontWeight={params.row.isSummary === 1 ? "700" : "500"}
            sx={{ pl: params.row._level * 2 }}
          >
            {params.value || ""}
          </Typography>
        );
      },
    },
    {
      field: "accountName",
      headerName: "Account Name",
      flex: 2.5,
      renderCell: (params) => {
        if (params.row.isCategoryHeader) return null;
        return (
          <Typography
            variant="body2"
            fontWeight={params.row.isSummary === 1 ? "700" : "500"}
          >
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: "subCategory",
      headerName: "Account Description",
      flex: 2,
      renderCell: (params) => {
        if (params.row.isCategoryHeader) return null;
        return (
          <Typography variant="body2" color={colors.grey[300]}>
            {params.value || "-"}
          </Typography>
        );
      },
    },
    {
      field: "category",
      headerName: "Category",
      flex: 1.5,
      renderCell: (params) => {
        if (params.row.isCategoryHeader) return null;
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              backgroundColor: colors.blueAccent[800],
              color: colors.grey[100],
              fontWeight: 500,
              fontSize: "0.75rem",
            }}
          />
        );
      },
    },
    {
      field: "normalBalanceType",
      headerName: "Balance Type",
      width: 130,
      renderCell: (params) => {
        if (params.row.isCategoryHeader) return null;
        const isDebit = params.value === "Debit";
        return (
          <Chip
            label={params.value}
            size="small"
            icon={isDebit ? <TrendingUpIcon /> : <TrendingDownIcon />}
            sx={{
              backgroundColor: isDebit ? colors.greenAccent[700] : colors.redAccent[700],
              color: colors.grey[100],
              fontWeight: 600,
            }}
          />
        );
      },
    },
    {
      field: "formattedBalance",
      headerName: "Balance",
      width: 160,
      renderCell: (params) => {
        if (params.row.isCategoryHeader) return null;
        return (
          <Typography variant="body2" fontWeight="700" color={colors.greenAccent[400]}>
            {params.value || "KES 0.00"}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (params.row.isCategoryHeader) return null;
        return (
          <Tooltip title="View journal entries" arrow>
            <IconButton
              size="small"
              onClick={() => handleBalanceClick(params.row)}
              sx={{
                color: colors.blueAccent[400],
                backgroundColor: colors.primary[400],
                "&:hover": {
                  backgroundColor: colors.primary[300],
                  transform: "scale(1.1)",
                  transition: "transform 0.2s ease",
                },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  const processedRows = getRowsWithStaticHeaders();
  const filteredRows = processedRows.filter((row) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      row.accountName?.toLowerCase().includes(searchLower) ||
      row.accountNumber?.toLowerCase().includes(searchLower) ||
      row.subCategory?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={48} sx={{ color: colors.greenAccent[500] }} />
        <Typography variant="body1" sx={{ color: colors.grey[300] }}>
          Loading chart of accounts...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Enhanced Header Section */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
          borderRadius: "16px",
          overflow: "visible",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography
                variant="h4"
                fontWeight="700"
                sx={{
                  color: colors.grey[100],
                  mb: 0.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <AccountTreeIcon sx={{ fontSize: 32 }} />
                Chart of Accounts
                <Chip
                  label={`${chartData?.length || 0} accounts`}
                  size="small"
                  sx={{
                    backgroundColor: colors.greenAccent[600],
                    color: "white",
                    fontWeight: 600,
                  }}
                />
              </Typography>
              <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                Complete listing of all general ledger accounts organized by category
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <Tooltip title="Refresh data" arrow>
                <IconButton
                  onClick={fetchChartOfAccounts}
                  sx={{
                    backgroundColor: colors.primary[300],
                    color: colors.grey[100],
                    "&:hover": {
                      backgroundColor: colors.primary[200],
                      transform: "rotate(180deg)",
                      transition: "transform 0.3s ease",
                    },
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export data" arrow>
                <IconButton
                  sx={{
                    backgroundColor: colors.primary[300],
                    color: colors.grey[100],
                    "&:hover": { backgroundColor: colors.primary[200] },
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Search Bar */}
      <Card
        elevation={0}
        sx={{
          mb: 2,
          borderRadius: "12px",
          border: `1px solid ${colors.primary[300]}`,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search accounts by name, number, or description..."
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: colors.primary[400],
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: colors.primary[300],
                },
                "&.Mui-focused": {
                  backgroundColor: colors.primary[300],
                  boxShadow: `0 0 0 3px ${colors.blueAccent[700]}30`,
                },
                "& fieldset": {
                  borderColor: colors.primary[300],
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.grey[300] }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm("")}
                    sx={{ color: colors.grey[300] }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Enhanced DataGrid */}
      <Card
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: `1px solid ${colors.primary[300]}`,
          overflow: "hidden",
        }}
      >
        <Box sx={{ height: "70vh" }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={getRowId}
            components={{ Toolbar: GridToolbar }}
            disableRowSelectionOnClick
            initialState={{
              pagination: {
                paginationModel: { pageSize: 50 },
              },
            }}
            pageSizeOptions={[25, 50, 100, 200]}
            sx={{
              border: "none",
              "& .MuiDataGrid-root": {
                border: "none",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: `1px solid ${colors.primary[300]}`,
                borderRight: "none",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: colors.primary[400],
                borderBottom: `2px solid ${colors.primary[300]}`,
                fontSize: "14px",
                fontWeight: 700,
                color: colors.grey[100],
              },
              "& .MuiDataGrid-virtualScroller": {
                backgroundColor: colors.primary[500],
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: `2px solid ${colors.primary[300]}`,
                backgroundColor: colors.primary[400],
              },
              "& .MuiDataGrid-row": {
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: colors.primary[400],
                  transform: "scale(1.001)",
                  boxShadow: `0 2px 8px ${colors.primary[900]}30`,
                },
              },
              "& .MuiDataGrid-toolbarContainer": {
                padding: "16px",
                backgroundColor: colors.primary[400],
                borderBottom: `1px solid ${colors.primary[300]}`,
                "& .MuiButton-text": {
                  color: colors.grey[100],
                  fontWeight: 600,
                },
              },
            }}
            hideFooterSelectedRowCount
          />
        </Box>
      </Card>

      {/* Journal Entries Modal */}
      <JournalEntriesModal
        open={journalModal.open}
        onClose={handleCloseModal}
        accountId={journalModal.accountId}
        accountName={journalModal.accountName}
        loading={journalModal.loading}
        journalData={journalModal.journalData}
      />
    </Box>
  );
};

export default ChartOfAccountsTable;